import React, { createContext, useContext, useEffect, useState } from 'react';
import { getTimezone } from '../api/client';

/**
 * The single source of the admin-configured display timezone.
 *
 * Fetched once (GET /api/v1/settings/timezone, which resolves the caller's
 * vendor setting server-side — see server/services/timezone_service.py) and
 * shared via context so every page reads the same value instead of each
 * page hitting the API independently or falling back to a per-browser
 * preference. Defaults to Asia/Kolkata while loading, matching the backend
 * default so there's no UTC flash before the real value arrives.
 */

const DEFAULT_TIMEZONE = 'Asia/Kolkata';

const TimezoneContext = createContext({ timezone: DEFAULT_TIMEZONE, loading: true, refresh: () => {} });

export function TimezoneProvider({ children }) {
  const [timezone, setTimezone] = useState(DEFAULT_TIMEZONE);
  const [loading, setLoading] = useState(true);

  const refresh = () => {
    setLoading(true);
    getTimezone()
      .then((data) => setTimezone(data?.timezone || DEFAULT_TIMEZONE))
      .catch(() => setTimezone(DEFAULT_TIMEZONE))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!localStorage.getItem('access_token')) { setLoading(false); return; }
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <TimezoneContext.Provider value={{ timezone, loading, refresh }}>
      {children}
    </TimezoneContext.Provider>
  );
}

/** Returns the configured display timezone string, e.g. "Asia/Kolkata". */
export default function useTimezone() {
  return useContext(TimezoneContext).timezone;
}

/** For SettingsPage: also needs loading state and a way to refetch after save. */
export function useTimezoneContext() {
  return useContext(TimezoneContext);
}
